import React, {useEffect, useRef, useState} from 'react'
import { useForm } from "react-hook-form";
import {useTranslation} from "react-i18next";

import Typography from '@mui/material/Typography';
import VisibilityOff from '@mui/icons-material/VisibilityOffSharp';
import { RemoveRedEye as RemoveRedEyeIcon } from '@vedmarket/ui-kit';
import Link from '@mui/material/Link';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField'
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import InputAdornment from "@mui/material/InputAdornment";

import {
  useCreateLeadByEmailMutation,
  useGetLeadByEmailMutation,
  useUpdateLeadStatusMutation
} from "../../../api/bitrix";
import { setToken, useSignInMutation, useSignUpMutation } from "../../../api/auth";
import { useAuthContext } from "../../../context/AuthContext";
import { Header, Notification, NOTIFICATION_TYPE } from '@vedmarket/ui-kit';
import {BITRIX_USER_STATUS, ROUTES} from "../../../config/constants";
import {EMAIL_PATTERN, PASSWORD_PATTERN} from "../../../utils/validation";
import {useLazyGetDictionaryLanguagesQuery} from "../../../api/localization";
import {changeAppLanguage, getLanguageCode} from "../../../config/i18";

import './../auth.scss'
import './register.scss'
import {Collapse} from "@mui/material";

const BITRIX_SYNC_TIMEOUT_MS = 5000;

enum REGISTER_FORM {
  FIRST_NAME = 'first_name',
  LAST_NAME = 'last_name',
  EMAIL = 'email',
  PASSWORD = 'password',
  PASSWORD2 = 'password2',
  AGREED = 'agreed',
}

type RegisterForm = {
  first_name: string
  last_name: string
  email: string
  password: string
  password2: string
  agreed: boolean
}

const Register = () => {

  const { t, i18n } = useTranslation()

  const { user, isSuccess } = useAuthContext();

  const [ localizationGetLanguagesRequest, { data: languages } ] = useLazyGetDictionaryLanguagesQuery();

  useEffect(() => {
    localizationGetLanguagesRequest('');
  }, [ localizationGetLanguagesRequest ]);

  useEffect(() => {
    if (isSuccess && user) {
      window.location.replace('/');
    }
  }, [ user, isSuccess ])

  const [ signUp ] = useSignUpMutation()

  const [ signIn ] = useSignInMutation()

  const [ getLeadByEmail ] = useGetLeadByEmailMutation();
  const [ updateLeadStatus ] = useUpdateLeadStatusMutation();
  const [ createLead ] = useCreateLeadByEmailMutation()

  const [ isVisiblePassword, setIsVisiblePassword ] = useState(false);
  const [ isVisiblePasswordConfirm, setIsVisiblePasswordConfirm ] = useState(false);

  const [ isLoading, setIsLoading ] = useState(false);
  const registeredEmail = useRef<string>();

  const [ commonError, setCommonError ] = useState(false);

  const { register, watch, getValues, handleSubmit, trigger, setError, clearErrors, formState: { errors, isValid } } = useForm<RegisterForm>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      [REGISTER_FORM.AGREED]: true,
    }
  })

  const selectedLanguage = languages?.find(({ code }) => getLanguageCode(code) === getLanguageCode(i18n.language))?.code;

  const checkIsValid = () => {
    const agreedValue = watch(REGISTER_FORM.AGREED) ?? false
    return isValid && agreedValue
  }

  const validatePasswordConfirm = (value: string) => (
    value === getValues(REGISTER_FORM.PASSWORD)
      ? true
      : t('errors__message__passwordsDoNotMatch')
  )

  useEffect(() => {
    if (errors[REGISTER_FORM.PASSWORD2]) {
      trigger(REGISTER_FORM.PASSWORD2);
    }
  }, [ errors, i18n.language, trigger ]);

  const handleChangeFirstName = () => {
    if (errors[REGISTER_FORM.FIRST_NAME]?.type === 'custom') {
      clearErrors(REGISTER_FORM.FIRST_NAME);
    }
  }

  const syncBitrixLead = async ({ email, first_name }: Pick<RegisterForm, 'email' | 'first_name'>) => {
    const lookup = getLeadByEmail({
      filter: { EMAIL: email },
      select: [ 'ID', 'TITLE', 'STATUS_ID', 'EMAIL' ],
    });
    let abortRequest = lookup.abort;
    const timeoutId = window.setTimeout(() => abortRequest(), BITRIX_SYNC_TIMEOUT_MS);

    try {
      const { result: leads } = await lookup.unwrap();
      const leadId = leads.find(({ STATUS_ID }) => STATUS_ID === BITRIX_USER_STATUS.IN_PROCESS)?.ID;

      const write = leadId
        ? updateLeadStatus({
          id: leadId,
          fields: { STATUS_ID: BITRIX_USER_STATUS.PROCESSED },
        })
        : createLead({
          fields: {
            EMAIL: [ { VALUE: email, VALUE_TYPE: "WORK" } ],
            NAME: first_name,
            STATUS_ID: BITRIX_USER_STATUS.PROCESSED,
          },
        });

      abortRequest = write.abort;
      await write.unwrap();
    } catch {
      // CRM is optional once the account has been created and authenticated.
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  const onSubmit = handleSubmit (async (values) => {
    setIsLoading(true);
    setCommonError(false);

    try {
      // Sign-in can be retried after account creation succeeds.
      if (registeredEmail.current !== values.email) {
        const response: any = await signUp(values);

        if (response.error) {
          const fields = response.error.data;

          if (fields?.first_name) {
            setError(REGISTER_FORM.FIRST_NAME, { type: "custom", message: fields.first_name[0] });
          }
          if (fields?.email) {
            setError(REGISTER_FORM.EMAIL, { type: "custom", message: fields.email[0] });
          }
          if (fields?.password) {
            setError(REGISTER_FORM.PASSWORD, { type: "custom", message: fields.password[0] });
          }

          if (fields?.first_name || fields?.email || fields?.password) {
            return;
          }

          throw response.error;
        }

        registeredEmail.current = values.email;
      }

      clearErrors([ REGISTER_FORM.FIRST_NAME, REGISTER_FORM.EMAIL, REGISTER_FORM.PASSWORD ]);

      const { jwt } = await signIn({
        email: values.email,
        password: values.password,
      }).unwrap();

      await syncBitrixLead(values);

      // Publishing the token can make the auth provider redirect the page.
      setToken(jwt);
      window.location.replace(`/${ROUTES.CABINET}`);
    } catch (error) {
      setCommonError(true);

      setTimeout(() => {
        setCommonError(false)
      }, 5000);

    } finally {
      setIsLoading(false);
    }
  })

  return (
    <>
      <Header
        isEmpty
        languages={languages}
        user={{ lang: selectedLanguage || getLanguageCode(i18n.language) } as MeParamsOutput}
        changeLanguage={changeAppLanguage}
      />
      <form className='auth registration' onSubmit={onSubmit}>
        <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: '-1.15px' }}>
          {t('common__text__registration')}
        </Typography>
        <Typography variant='body1' sx={{ marginTop: 2, marginBottom: 3 }}>
          {t('actions__message__getStarted')}
        </Typography>
        <Card sx={{
          boxShadow: 'none',
        }}>
          <CardContent className='auth__card' sx={{ padding: 3 }}>
            <div className='registration__form'>
              <TextField
                {...register(REGISTER_FORM.FIRST_NAME, {
                  required: t('common__text__isRequired'),
                  onChange: handleChangeFirstName,
                })}
                error={Boolean(errors[REGISTER_FORM.FIRST_NAME])}
                variant='outlined'
                label={t('common__text__firstName')}
                helperText={errors[REGISTER_FORM.FIRST_NAME]?.message}
              />

              <TextField
                {...register(REGISTER_FORM.EMAIL, { required: t('common__text__isRequired'), pattern: { value: EMAIL_PATTERN, message: t('errors__message__incorrectEmail')} })}
                error={Boolean(errors[REGISTER_FORM.EMAIL])}
                variant='outlined'
                label={t('common__text__email')}
                className='registration__email'
                helperText={errors[REGISTER_FORM.EMAIL]?.message}
              />

              <div className='registration__password'>
                <TextField
                  {...register(REGISTER_FORM.PASSWORD, {
                    required: t('common__text__isRequired'),
                    pattern: { value: PASSWORD_PATTERN, message: t('errors__message__incorrectPassword')}
                  })}
                  error={Boolean(errors[REGISTER_FORM.PASSWORD])}
                  variant='outlined'
                  label={t('common__text__password')}
                  type={isVisiblePassword? 'text' : 'password'}
                  helperText={errors[REGISTER_FORM.PASSWORD]?.message}
                  InputProps={{
                    endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePassword(!isVisiblePassword)} className='auth__input-adornment'>{isVisiblePassword ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
                  }}
                  onBlur={async (e) => {

                    await register(REGISTER_FORM.PASSWORD).onBlur(e);

                    if (getValues(REGISTER_FORM.PASSWORD2)) {
                      await trigger([ REGISTER_FORM.PASSWORD2 ])
                    }
                  }}
                  sx={{ width: 'calc(100% / 2 - 12px)' }}
                />
                <TextField
                  {...register(REGISTER_FORM.PASSWORD2, {
                    required: t('common__text__isRequired'),
                    pattern: { value: PASSWORD_PATTERN, message: t('errors__message__incorrectPassword')},
                    validate: value => validatePasswordConfirm(value)
                  })}
                  error={Boolean(errors[REGISTER_FORM.PASSWORD2])}
                  variant='outlined'
                  label={t('common__text__confirmPassword')}
                  type={isVisiblePasswordConfirm? 'text' : 'password'}
                  helperText={errors[REGISTER_FORM.PASSWORD2]?.message}
                  InputProps={{
                    endAdornment: <InputAdornment position='start' onClick={() => setIsVisiblePasswordConfirm(!isVisiblePasswordConfirm)} className='auth__input-adornment'>{isVisiblePasswordConfirm ? <VisibilityOff/> : <RemoveRedEyeIcon/>}</InputAdornment>,
                  }}
                  sx={{ width: 'calc(100% / 2 - 12px)' }}
                />
              </div>
            </div>
            <Button
              variant='contained'
              sx={{
                marginTop: '12px',
                marginBottom: '4px',
              }}
              disabled={!checkIsValid() || isLoading}
              type='submit'
            >{isLoading ? t('common__message__loadingText') : t('actions__text__signup')}</Button>
            <Typography variant='body2' sx={{ marginTop: '14px' }}>
              {t('actions__text__iAccept')}&nbsp;
              <a href='https://vedmarket.ru/agreement' target='_blank' className='registration__linkAccept'>
                {t('actions__text__ourPrivacyPolicy')}
              </a>.
            </Typography>
            <Collapse in={Boolean(commonError)} timeout={400}>
              <Notification type={NOTIFICATION_TYPE.ERROR} title={t('errors__message__commonError')} />
            </Collapse>
          </CardContent>
        </Card>
        <Typography variant='body1' sx={{ textAlign: 'center', marginTop: '28px' }}>
          {t('actions__message__alreadyAMember')}&nbsp;
          <Link href={ROUTES.LOGIN}>{t('actions__text__login')}</Link>
        </Typography>
      </form>
    </>
  )
}

export default Register
